import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, ShoppingBag, Gift, Menu } from 'lucide-react';
import { createPageUrl } from './utils';
import SideDrawer from './components/SideDrawer';
import DesktopNavbar from './components/DesktopNavbar';
import Footer from './components/Footer';
import { isCustomDomain } from './components/AppStoreBadges';
import MobileHeader from './components/MobileHeader';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { useNavigationStack } from './lib/useNavigationStack';

export default function Layout({ children, currentPageName }) {
  const location = useLocation();
  const navigate = useNavigate();
  // Coming back from a page opened via the menu: start with the menu already open (no slide-in).
  const [restoreMenu] = useState(() => !!sessionStorage.getItem('reopenMenu'));
  const [drawerOpen, setDrawerOpen] = useState(restoreMenu);
  const [contactInfo, setContactInfo] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [squareCustomerId, setSquareCustomerId] = useState(null);
  // The signed-in user comes from AuthContext, which already resolved it once.
  // Calling base44.auth.me() here again produced a duplicate 401 in the console
  // on every page load for signed-out visitors.
  const { user, isLoadingAuth } = useAuth();
  const { handleTabSelect } = useNavigationStack(currentPageName || 'Home', `/${currentPageName || 'Home'}`);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (sessionStorage.getItem('reopenMenu')) {
      sessionStorage.removeItem('reopenMenu');
      sessionStorage.removeItem('cameFromMenu');
      setDrawerOpen(true);
      return;
    }
  }, [location.pathname]);

  useEffect(() => {
    const loadContactInfo = async () => {
      try {
        // Read from HomeCache (single entity, already maintained by admin saves)
        const cache = await base44.entities.HomeCache.list();
        if (cache[0]?.contact_info) {
          setContactInfo(cache[0].contact_info);
        }
      } catch (error) {
        // Silently handle error
      }
    };
    loadContactInfo();
  }, []);

  // Load user once, then set up unread count + popup checks
  useEffect(() => {
    let unsubscribe;

    const init = async () => {
      try {
        if (isLoadingAuth) return;
        const custId = user?.square_customer_id;
        if (!custId) return;
        setSquareCustomerId(custId);

        // Load unread count
        const notifications = await base44.entities.Notification.filter({
          customer_id: custId,
          is_read: false
        });
        const now = new Date().toISOString();
        setUnreadCount(notifications.filter((n) => !n.expires_at || n.expires_at > now).length);

        // Notifications are never surfaced automatically — the Notification
        // Centre is the only place they're displayed. The unread badge below is
        // the only on-screen signal.

        // Subscribe — filter events to this user only
        unsubscribe = base44.entities.Notification.subscribe((event) => {
          if (event.data?.customer_id && event.data.customer_id !== custId) return;

          if (event.type === 'create') {
            setUnreadCount((prev) => prev + 1);
          } else if (event.type === 'update' && event.data?.is_read === true) {
            setUnreadCount((prev) => Math.max(0, prev - 1));
          }
        });
      } catch (error) {
        // Silently handle
      }
    };

    init();
    return () => { if (unsubscribe) unsubscribe(); };
  }, [user?.square_customer_id, isLoadingAuth]);

  const navItems = [
  // Native app (Base44 domain) goes to App Home; the website keeps /Home.
  { name: 'Home', icon: Home, path: window.location.hostname.endsWith('base44.app') ? '/AppHome' : createPageUrl('Home') },
  { name: 'Order', icon: ShoppingBag, path: createPageUrl('Order') },
  { name: 'Loyalty', icon: Gift, path: createPageUrl('Loyalty') },
  { name: 'Menu', icon: Menu, action: () => setDrawerOpen(true) }];

  const handleNavClick = (item) => {
    if (item.action) {
      item.action();
    } else {
      setDrawerOpen(false);
      handleTabSelect(isActive(item.name));
    }
  };


  const isActive = (itemName) => {
    return currentPageName?.toLowerCase() === itemName.toLowerCase();
  };

  return (
    <div className="min-h-screen bg-white" onClick={() => drawerOpen && setDrawerOpen(false)}>
      <DesktopNavbar onMenuClick={() => setDrawerOpen(true)} currentPageName={currentPageName} notificationCount={unreadCount} />
      
      <main className={`relative page-content ${isCustomDomain() && (currentPageName?.toLowerCase() === 'home' || !currentPageName) ? 'page-content-tight' : ''} ${!isCustomDomain() && currentPageName?.toLowerCase() !== 'home' && currentPageName?.toLowerCase() !== 'order' && currentPageName?.toLowerCase() !== 'loyalty' && currentPageName?.toLowerCase() !== 'events' ? 'native-top' : ''} ${isCustomDomain() && currentPageName?.toLowerCase() !== 'home' ? 'web-top' : ''}`}>
        {typeof children === 'object' && children.type ?
        React.cloneElement(children, { onMenuClick: () => setDrawerOpen(true) }) :
        children}
      </main>

      {!isCustomDomain() && currentPageName?.toLowerCase() !== 'home' && currentPageName?.toLowerCase() !== 'order' && currentPageName?.toLowerCase() !== 'loyalty' && currentPageName?.toLowerCase() !== 'events' &&
      <div aria-hidden="true" className="ios-status-bg lg:hidden fixed top-0 left-0 right-0 bg-white z-header pointer-events-none" />
      }

      <SideDrawer instant={restoreMenu} isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} onNavigate={handleTabSelect} />

      {(currentPageName?.toLowerCase() === 'home' || !currentPageName) &&
      <Footer contactInfo={contactInfo} />
      }

      {/* Bottom Navigation - Fixed and Always on Top (Mobile Only) */}
      <nav aria-label="Main navigation" className="bottom-nav lg:hidden fixed bottom-0 left-0 right-0 border-t border-black/10 shadow-lg z-nav bg-white pointer-events-auto">
        <div className="sm:px-6 pr-4 pl-4">
          <div className="flex items-center justify-around">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.name);

              // Active state is signalled without relying on colour alone:
              // a top indicator bar, a bolder label and a thicker icon stroke.
              const content =
              <>
                  <span
                  aria-hidden="true"
                  className={`absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-8 rounded-full ${active ? 'bg-black' : 'bg-transparent'}`} />

                  <Icon
                  aria-hidden="true"
                  strokeWidth={active ? 2.75 : 2}
                  className="w-6 h-6 sm:w-7 sm:h-7 mb-2 transition-colors pointer-events-none text-black" />
                
                  <span className="text-[10px] sm:text-xs pointer-events-none font-medium text-black">
                    {item.name}
                  </span>
                </>;


              if (item.action) {
                return (
                  <button
                    key={item.name}
                    onClick={item.action}
                    aria-label="Open menu"
                    aria-expanded={drawerOpen}
                    className="relative flex flex-col items-center justify-center py-4 sm:py-5 px-3 sm:px-4 transition-all min-w-[60px] min-h-[60px] bg-transparent border-0 cursor-pointer active:opacity-70 rounded-lg focus-visible:ring-2 focus-visible:ring-pink-300 focus-visible:ring-offset-2 focus-visible:outline-none">
                    
                    {content}
                  </button>);

              }

              return (
                <Link
                  key={item.name}
                  to={item.path}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => setDrawerOpen(false)}
                  className="relative flex flex-col items-center justify-center py-4 sm:py-5 px-3 sm:px-4 transition-all min-w-[60px] min-h-[60px] active:opacity-70 rounded-lg focus-visible:ring-2 focus-visible:ring-pink-300 focus-visible:ring-offset-2 focus-visible:outline-none">
                  
                  {content}
                  {active && <span className="sr-only">(current page)</span>}
                </Link>);

            })}
          </div>
        </div>
      </nav>
    </div>);

}